import { D } from "@netkeep/fee-engine";
import type { CalculationResult } from "@netkeep/fee-types";
import type { ReactNode } from "react";
import { isNegative, money, percent, TAX_COLLECTOR_LABEL } from "@/lib/format";
import { Badge, Card } from "./ui";

type GroupKind = "revenue" | "fees" | "costs" | "profit";

/**
 * Itemized breakdown, grouped as Revenue → Fees → Costs → Profit, each with
 * its own color so the four kinds of money are never confused.
 */
export function Breakdown({ result, embedded = false }: { result: CalculationResult; embedded?: boolean }) {
  const c = result.currency;
  const nonZeroExpenses = result.expenses.filter((e) => e.amount !== "0.00");
  const body = (
    <div className="breakdown">
      <Group title="Revenue" kind="revenue">
        <Line label="Item price" value={money(result.revenue.itemPrice, c)} />
        <Line label="Shipping charged to buyer" value={money(result.revenue.shippingCharged, c)} />
        <Line label="Seller revenue" value={money(result.revenue.sellerRevenue, c)} strong />
        {result.revenue.salesTax !== "0.00" ? (
          <Line
            label="Sales tax (not revenue)"
            value={money(result.revenue.salesTax, c)}
            detail={TAX_COLLECTOR_LABEL[result.taxCollectedBy]}
            muted
          />
        ) : null}
      </Group>

      <Group title="Fees" kind="fees">
        {result.fees.length === 0 ? <p className="muted">No fees for this profile.</p> : null}
        {result.fees.map((f) => (
          <div key={f.code} className="stack-xs">
            <div className="line">
              <span className="fee-label">
                <span className="line__label">{f.label}</span>
                <Badge label={f.source === "PROFILE" ? "Preset" : "Manual"} tone={f.source === "PROFILE" ? "info" : "warning"} />
              </span>
              <span className="line__value">−{money(f.amount, c)}</span>
            </div>
            <span className="line__detail">{f.explanation}</span>
          </div>
        ))}
        <Line label="Total fees" value={`−${money(result.totalFees, c)}`} strong />
      </Group>

      <Group title="Costs" kind="costs">
        <Line label="Item cost" value={`−${money(result.itemCost, c)}`} />
        {nonZeroExpenses.map((e) => (
          <Line key={e.code} label={e.label} value={`−${money(e.amount, c)}`} />
        ))}
        <Line label="Total costs" value={`−${money(new D(result.totalExpenses).plus(result.itemCost).toFixed(), c)}`} strong />
      </Group>

      <Group title="Result" kind="profit">
        <Line label="Net payout from platform" value={money(result.netPayout, c)} />
        {result.taxLiability !== "0.00" ? (
          <Line label="Tax you must remit" value={`−${money(result.taxLiability, c)}`} muted />
        ) : null}
        <Line
          label="Net profit"
          value={money(result.netProfit, c)}
          strong
          tone={isNegative(result.netProfit) ? "is-negative" : "is-profit"}
        />
        <Line label="Margin" value={percent(result.profitMarginPercent)} muted />
        <Line label="Return on cost (ROI)" value={percent(result.roiPercent)} muted />
        <Line label="Effective fee rate" value={percent(result.effectiveFeePercent)} muted />
        <Line
          label="Break-even sale price"
          value={result.breakEvenPrice != null ? money(result.breakEvenPrice, c) : "Not reachable"}
          muted
        />
      </Group>

      {result.warnings.map((w) => (
        <p key={w} className="text-warning" role="alert">
          ⚠ {w}
        </p>
      ))}
      <p className="line__detail">
        Profit = seller revenue − fees − costs. Fees are rounded to the cent (half-up). Formula v{result.formulaVersion}.
      </p>
    </div>
  );

  if (embedded) return body;
  return (
    <Card>
      <h2 className="heading">Breakdown</h2>
      {body}
    </Card>
  );
}

function Group({ title, kind, children }: { title: string; kind: GroupKind; children: ReactNode }) {
  return (
    <section className={`group group--${kind}`} aria-label={title}>
      <h3 className="group__title">{title}</h3>
      {children}
    </section>
  );
}

function Line({
  label,
  value,
  detail,
  strong,
  muted,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  strong?: boolean;
  muted?: boolean;
  /** Color class for the value, e.g. profit vs. loss. */
  tone?: "is-profit" | "is-negative";
}) {
  const cls = ["line", strong && "line--strong", muted && "line--muted"].filter(Boolean).join(" ");
  return (
    <div>
      <div className={cls}>
        <span className="line__label">{label}</span>
        <span className={tone ? `line__value ${tone}` : "line__value"}>{value}</span>
      </div>
      {detail ? <span className="line__detail">{detail}</span> : null}
    </div>
  );
}
